<template>
  <div class="container">
    <div class="row">
      <div class="col-md-8 offset-md-2">
        <div v-if="receipt">
          <div class="card mb-5" id="receipt">
            <div class="card-body">
              <h5 class="card-title">
                <span v-if="receipt.transactionType === 'sale'"
                  >Sale receipt</span
                >
                <span v-else-if="receipt.transactionType === 'refund'"
                  >Refund receipt</span
                >
                <span v-if="receipt.transactionType === 'cancellation'"
                  >Authorization cancellation receipt</span
                >
              </h5>
              <h6 class="card-subtitle mb-2 text-muted">
                <time
                  v-if="receipt.details.capturedAt"
                  :datetime="receipt.details.capturedAt"
                >
                  {{ receipt.details.capturedAt | moment(dateDisplayFormat) }}
                </time>
              </h6>
              <ul class="list-group list-group-flush">
                <li class="list-group-item">
                  <span v-if="receipt.details.headerLine1">
                    {{ receipt.details.headerLine1 }}
                    <br />
                  </span>
                  <span v-if="receipt.details.headerLine2">
                    {{ receipt.details.headerLine2 }}
                    <br />
                  </span>
                  <span v-if="receipt.details.headerLine3">
                    {{ receipt.details.headerLine3 }}
                    <br />
                  </span>
                  <span v-if="receipt.details.headerLine4">
                    {{ receipt.details.headerLine4 }}
                    <br />
                  </span>
                  <span v-if="receipt.details.headerLine5">
                    {{ receipt.details.headerLine5 }}
                    <br />
                  </span>
                </li>

                <li class="list-group-item">
                  <table class="table table-borderless table-sm amounts m-0">
                    <tbody>
                      <tr v-if="receipt.details.saleAmount">
                        <th>Amount</th>
                        <td>
                          <vue-intl-numberformat
                            formatStyle="currency"
                            :currency="receipt.details.currency"
                            :number="receipt.details.saleAmount / 100"
                          ></vue-intl-numberformat>
                        </td>
                      </tr>
                      <tr v-if="receipt.details.gratuityAmount">
                        <th>Tip</th>
                        <td>
                          <vue-intl-numberformat
                            formatStyle="currency"
                            :currency="receipt.details.currency"
                            :number="receipt.details.gratuityAmount / 100"
                          ></vue-intl-numberformat>
                        </td>
                      </tr>
                      <tr v-if="receipt.details.commissionAmount">
                        <th>Processing charge</th>
                        <td>
                          <vue-intl-numberformat
                            formatStyle="currency"
                            :currency="receipt.details.currency"
                            :number="receipt.details.commissionAmount / 100"
                          ></vue-intl-numberformat>
                        </td>
                      </tr>
                      <tr v-if="receipt.details.totalAmount">
                        <th>Total</th>
                        <td>
                          <vue-intl-numberformat
                            formatStyle="currency"
                            :currency="receipt.details.currency"
                            :number="receipt.details.totalAmount / 100"
                          ></vue-intl-numberformat>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </li>
                <li class="list-group-item">
                  <!-- Card scheme icon -->
                  <span v-if="receipt.details.cardScheme">
                    <span class="mr-2">{{
                      humaniseCardScheme(receipt.details.cardScheme)
                    }}</span>
                    <span v-if="cardScheme(receipt) === 'amex'">
                      <font-awesome-icon
                        :icon="['fab', 'cc-amex']"
                      ></font-awesome-icon>
                    </span>
                    <span v-else-if="cardScheme(receipt) === 'visa'">
                      <font-awesome-icon
                        :icon="['fab', 'cc-visa']"
                      ></font-awesome-icon>
                    </span>
                    <span v-else-if="cardScheme(receipt) === 'mastercard'">
                      <font-awesome-icon
                        :icon="['fab', 'cc-mastercard']"
                      ></font-awesome-icon>
                    </span>
                    <span v-else>
                      <font-awesome-icon icon="credit-card"></font-awesome-icon>
                    </span>
                  </span>
                  <!-- Card PAN -->
                  <span v-if="receipt.details.panLength">
                    {{ "*".repeat(receipt.details.panLength - 4) }}
                  </span>
                  <span v-if="receipt.details.panLastFour">
                    {{ receipt.details.panLastFour }}
                  </span>
                  <br />
                  <span v-if="receipt.details.interface">
                    {{ receipt.details.interface | humanize }}
                  </span>
                  <span v-if="receipt.details.cardholderVerificationMethod">
                    ({{
                      receipt.details.cardholderVerificationMethod | humanize
                    }})
                  </span>
                </li>
                <li class="list-group-item">
                  <span v-if="receipt.details.status">
                    {{ receipt.details.status | humanize }}
                  </span>
                  <span v-if="receipt.details.actionCode"
                    >with action code {{ receipt.details.actionCode }}</span
                  >

                  <span v-if="receipt.details.statusExplanation"
                    >({{ receipt.details.statusExplanation }})</span
                  >

                  <br />
                  <span v-if="receipt.details.authCode"
                    >Authorization code: {{ receipt.details.authCode }}</span
                  >
                </li>
                <li class="list-group-item">
                  <table class="table table-borderless table-sm references m-0">
                    <tbody>
                      <tr v-if="receipt.details.merchantReference">
                        <th>Merchant</th>
                        <td>{{ receipt.details.merchantReference }}</td>
                      </tr>
                      <tr v-if="receipt.details.terminalSerialNumber">
                        <th>TSN</th>
                        <td>{{ receipt.details.terminalSerialNumber }}</td>
                      </tr>
                      <tr v-if="receipt.details.terminalReference">
                        <th>TID</th>
                        <td>{{ receipt.details.terminalReference }}</td>
                      </tr>
                      <tr v-if="receipt.details.retrievalReferenceNumber">
                        <th>RRN</th>
                        <td>{{ receipt.details.retrievalReferenceNumber }}</td>
                      </tr>
                      <tr
                        v-if="
                          receipt.details.transactionNumber &&
                            receipt.details.batchNumber
                        "
                      >
                        <th>TXN/BAT</th>
                        <td>
                          {{ receipt.details.transactionNumber }}/
                          {{ receipt.details.batchNumber }}
                        </td>
                      </tr>
                      <!-- EMV fields -->
                      <tr v-if="receipt.details.applicationIdentifier">
                        <th>AID</th>
                        <td class="text-truncate" style="max-width: 0;">
                          {{ receipt.details.applicationIdentifier }}
                        </td>
                      </tr>
                      <tr v-if="receipt.details.terminalVerificationResults">
                        <th>TVR</th>
                        <td class="text-truncate" style="max-width: 0;">
                          {{ receipt.details.terminalVerificationResults }}
                        </td>
                      </tr>
                      <tr v-if="receipt.details.issuerApplicationData">
                        <th>IAD</th>
                        <td class="text-truncate" style="max-width: 0;">
                          {{ receipt.details.issuerApplicationData }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </li>
              </ul>
              <p class="card-text lead m-4">Thank you for your purchase.</p>
              <!-- <a href="#" class="card-link no-print">
                Download PDF &nbsp;
                <font-awesome-icon icon="file-download"></font-awesome-icon>
              </a>-->
              <a href="#" class="card-link no-print" @click="print">
                Print &nbsp;
                <font-awesome-icon icon="print"></font-awesome-icon>
              </a>
            </div>
          </div>
        </div>
        <div v-else class="card">
          <div class="card-body">
            <div v-if="loading">
              <pulse-loader :loading="loading"></pulse-loader>
            </div>
            <div v-else>
              <div v-if="error" class="alert alert-warning" role="alert">
                Error: {{ error }}
              </div>

              <receipt-details-lookup
                :receipt-id="receiptId"
              ></receipt-details-lookup>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
table.amounts th {
  text-align: left;
}

table.amounts td {
  text-align: right;
}

@media print {
  .no-print,
  .no-print * {
    display: none !important;
  }
}
</style>

<script>
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import VueIntlNumberformat from "vue-intl-numberformat";
import printJS from "print-js";
import ReceiptDetailsLookup from "@/components/ReceiptDetailsLookup";

export default {
  name: "receiptSearch",
  components: {
    PulseLoader,
    VueIntlNumberformat,
    ReceiptDetailsLookup,
  },
  props: ["receiptId"],
  data() {
    return {
      panLastFour: "",
      receipt: null,
      loading: false,
      error: null,
      dateDisplayFormat: "ddd, Do MMM YYYY [at] h:mm A",
    };
  },
  watch: {
    panLastFour: function(val) {
      if (val && val.length === 4) {
        this.loadReceipt();
      }
    },
    $route(to) {
      if (to.query && to.query.panLastFour) {
        this.panLastFour = to.query.panLastFour;
      }
    },
  },
  methods: {
    loadReceipt() {
      this.loading = true;

      this.api
        .get(`v1/receipts/${this.receiptId}`, {
          params: {
            panLastFour: this.panLastFour,
          },
        })
        .then((response) => {
          this.loading = false;
          if (response.data) {
            this.receipt = response.data;

            // check the query param for print dialog
            this.$nextTick(() => {
              if (
                this.$route.query &&
                this.$route.query.print &&
                this.$route.query.print === "true"
              ) {
                this.print();
              }
            });
          }
        })
        .catch((err) => {
          this.loading = false;
          if (err.response && err.response.status === 410) {
            this.error = "This receipt has been archived and is no longer available.";
          } else if (err.response && err.response.data && err.response.data.message) {
            this.error = err.response.data.message;

            if (
              err.response.data.message ===
              "panLastFour query parameter is incorrect"
            ) {
              this.error = "Invalid card details, please try again.";
            }
          } else {
            this.error = "Failed to load receipt.";
          }
        });
    },
    cardScheme(receipt) {
      let scheme = "unknown";
      if (receipt && receipt.details && receipt.details.cardScheme) {
        scheme = receipt.details.cardScheme.toLowerCase();
      }
      return scheme;
    },
    humaniseCardScheme(input) {
      let output = "";
      if (input && typeof input === "string") {
        if (input.toLowerCase() === "visa") {
          output = "Visa";
        } else if (input.toLowerCase() === "mastercard") {
          output = "Mastercard";
        } else if (input.toLowerCase() === "amex" || input.toLowerCase() === "american_express") {
          output = "American Express";
        }
      }

      return output;
    },
    print() {
      printJS({
        printable: "receipt",
        type: "html",
        css:
          "https://stackpath.bootstrapcdn.com/bootstrap/4.2.1/css/bootstrap.min.css",
        style: ".no-print, .no-print * { display: none !important; }",
      });
    },
  },
  created() {
    // check the query param
    if (this.$route.query && this.$route.query.panLastFour) {
      this.panLastFour = this.$route.query.panLastFour;
      this.loadReceipt();
    }
  },
};
</script>
